import React, { useState } from 'react'
import { Tooltip2 } from '@blueprintjs/popover2'
import { Toaster } from '@ui'
import css from './CheckInButton.module.scss'
import cx from 'classnames'

import { get } from 'lodash'
import { DateUtil, TIME, hasSpaceEnforceQrCode, pluralize } from '@utils'

import { useAppData } from '@providers'

import api, { IBooking, ISpace } from '@api'
import { useReload } from '@hooks/reloadContext'
import { ApolloError } from 'apollo-client'
import useSettings from 'components/NewSettings/hooks/useSettings'
import HealthCheckContent from 'components/HealthCheckContent/HealthCheckContent'
import CheckinWithQRCodeModal from 'components/CheckinWithQRCodeModal/CheckinWithQRCodeModal'

const CheckInButton = ({
  booking,
  classStyle = {},
}: {
  booking: IBooking | undefined
  classStyle?: { checkedIn?: string; checkIn?: string; disabled?: string }
}) => {
  const [showHealthCheck, setShowHealthCheck] = useState(false)
  const [showQRCodeModal, setShowQRCodeModal] = useState(false)
  const {
    product: { features },
  } = useAppData()
  const { bookings } = useReload()
  const settings = useSettings()
  const enableHealthScreening =
    features.includes('healthCheckAcknowledgment') && settings.enableHealthScreening

  const passedHealthCheck = get(booking, 'passedHealthCheck')
  const hoursBeforeBookingCheckin = settings.hoursBeforeBookingCheckin
  const hoursHealthMessageSentBeforeBooking = settings.hoursHealthMessageSentBeforeBooking
  const qrModalEnabled = features.includes('qrCode') && settings.qrCodeEnabled
  const timezone = get(booking, 'space.floor.building.timezone')
  const startDate = DateUtil.tz(booking?.startDate, timezone)
  const endDate = DateUtil.tz(booking?.endDate, timezone)

  const nowDate = DateUtil.tz(timezone)
  const checkInWindowPassed = !endDate.isAfter(nowDate)
  const cannotCheckIn = !DateUtil().isAfter(
    startDate.clone().sub(hoursBeforeBookingCheckin, TIME.HOURS)
  )
  const checkInTime = startDate.diff(nowDate, TIME.MINUTES) <= 60 * hoursBeforeBookingCheckin
  const showHealthCheckButton =
    enableHealthScreening &&
    !passedHealthCheck &&
    !checkInTime &&
    startDate.diff(nowDate, TIME.MINUTES) <= hoursHealthMessageSentBeforeBooking * 60
  const bookinghealthCheck = booking?.healthCheck
  const checkinTimeStr = `${hoursBeforeBookingCheckin}${pluralize(hoursBeforeBookingCheckin, 'hr')}`
  const enforceQrCode =
    qrModalEnabled && hasSpaceEnforceQrCode(get(booking, 'space') as ISpace | undefined)

  const onHealthCheckClick = () => {
    if (enforceQrCode) {
      setShowQRCodeModal(true)
      return
    }
    setShowHealthCheck(true)
  }

  const onCheckInClick = (id: string | undefined, checkedIn: boolean) => {
    if (enforceQrCode) {
      setShowQRCodeModal(true)
      return
    }
    api.booking
      .checkIn({
        id: id,
        checkedIn: checkedIn,
      })
      .then(() => {
        bookings()
      })
      .catch((error: ApolloError) => {
        Toaster.fail({ error })
      })
  }

  return (
    <div className={css.checkInWrapper}>
      {showHealthCheck && (
        <HealthCheckContent
          booking={booking}
          settings={settings}
          onHide={() => setShowHealthCheck(false)}
        />
      )}

      {showQRCodeModal && <CheckinWithQRCodeModal onHide={() => setShowQRCodeModal(false)} />}
      {booking?.checkedIn ? (
        enableHealthScreening && passedHealthCheck ? (
          <Tooltip2 content="Health Check Passed" position="top">
            <div>
              <span className={cx(css.btnClass, classStyle.checkedIn || css.checkedIn)}>
                CHECKED-IN
              </span>
              <i className="fal fa-badge-check" />
            </div>
          </Tooltip2>
        ) : (
          <span className={cx(css.btnClass, classStyle.checkedIn || css.checkedIn)}>
            CHECKED-IN
          </span>
        )
      ) : !checkInWindowPassed &&
        enableHealthScreening &&
        !passedHealthCheck &&
        !bookinghealthCheck?.disabled ? (
        <div
          className={cx(
            css.btnClass,
            checkInWindowPassed || (!showHealthCheckButton && cannotCheckIn)
              ? classStyle.disabled || css.btnDisabledClass
              : classStyle.checkIn || css.checkInButtonClass
          )}
          data-button="admin_check_in_button"
          onClick={
            checkInWindowPassed || (!showHealthCheckButton && cannotCheckIn)
              ? () => {}
              : onHealthCheckClick
          }>
          {!checkInWindowPassed && showHealthCheckButton ? 'HEALTH CHECK' : 'CHECK-IN'}
        </div>
      ) : (
        <Tooltip2
          content={
            checkInWindowPassed
              ? 'Cannot Check-in for passed booking'
              : enforceQrCode
              ? 'Use mobile to scan qr code to checkin. In case of any concerns, please contact your admin.'
              : cannotCheckIn
              ? `Check-in is available ${checkinTimeStr} before the start of your booking`
              : 'If not checked in, Booking will be released 2 hours after it starts'
          }
          position="top">
          {/* have to wrap button in div so tooltip shows even when disabled */}
          <div>
            <div
              className={cx(
                css.btnClass,
                checkInWindowPassed || cannotCheckIn
                  ? classStyle.disabled || css.btnDisabledClass
                  : classStyle.checkIn || css.checkInButtonClass
              )}
              onClick={
                checkInWindowPassed || cannotCheckIn
                  ? () => {}
                  : () => onCheckInClick(booking?.id, true)
              }>
              CHECK-IN
            </div>
          </div>
        </Tooltip2>
      )}
    </div>
  )
}

export default CheckInButton
